import { JSX, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import Button from "@/components/buttons/Button.tsx";
import CircleButton from "@/components/buttons/CircleButton.tsx";
import { IconFacebook } from "@/components/icons";
import IconTelegram from "@/components/icons/IconTelegram.tsx";

import { queryClient } from "@/api/query-client.tsx";
import { USER_KEYS, useNotificationDetail } from "@/hooks/api/user.hooks.ts";
import { useLang } from "@/hooks/useLang.ts";
import { cn } from "@/utils/cn.ts";

const NotificationIdPage = (): JSX.Element => {
  const { t } = useTranslation();
  const { id } = useParams();
  const lang = useLang();

  const { data } = useNotificationDetail(parseInt(id!));

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [USER_KEYS.notificationsCount(lang)],
    });
  }, [lang]);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (divRef.current && buttonRef.current) {
      divRef.current.style.width = `${buttonRef.current.offsetWidth}px`;
    }
  }, []);

  const date = new Date(data.date_created);

  const formattedDate = date.toLocaleDateString("ru", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return (
    <section className={"flex items-start justify-center rounded-7.5 py-[3.75rem] max-xl:flex-col max-lg:py-6"}>
      <div className={"w-full max-w-[56.25rem] space-y-[1.875rem] max-2xl:max-w-[40rem] max-xl:mt-6 max-xl:max-w-none"}>
        <div>
          <h1 className={"text-[32px] font-semibold max-lg:text-2xl"}>{data.title}</h1>
          <p
            className={cn(
              "relative mt-5 pl-3 text-sm/4 uppercase",
              "max-lg:mt-2",
              "before:absolute before:left-0 before:top-1/2 before:size-1 before:-translate-y-1/2 before:transform before:rounded-full before:bg-orange-1 before:content-['']",
            )}
          >
            {formattedDate}
          </p>
        </div>
        <img
          src={data.picture.resized}
          alt={data.title}
          className={"aspect-[900/506] w-full rounded-7.5 object-cover object-center"}
        />
        <p className={"max-lg:text-sm"}>{data.description}</p>
        {data.link && (
          <Link to={data.link} className="block">
            <Button aria-label={t("Actions.go-to")}>{t("Actions.go-to")}</Button>
          </Link>
        )}
        <hr className={"h-px w-full border-surface-container"} />
        <div className={"flex items-center gap-5"}>
          <p className={"text-grey-1"}>{t("Actions.share-on-social")}</p>
          <div className={"flex items-center gap-4"}>
            <CircleButton icon={<IconTelegram className={"size-4 fill-on-primary"} />} buttonType={"social media"} />
            <CircleButton icon={<IconFacebook className={"size-4 fill-on-primary"} />} buttonType={"social media"} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default NotificationIdPage;
